import { useEffect, useState } from "react";
import { PageLoader } from "@/components/common/loader";
import {
  listNotices,
  listProformas,
  listPublishedDocuments,
  listRequiredDocuments,
  portalMe,
  type PortalNotice,
  type PortalProforma,
  type PortalRequiredDocument,
} from "@/features/portal/api";
import { usePortalSessionStore } from "@/features/portal/store/portal-session-store";
import type { PortalDocument } from "@/features/portal/types";
import { deriveHub } from "./derive";
import { WelcomeHero } from "./welcome-hero";
import { ChecklistCard } from "./checklist-card";
import { NeedsTasks } from "./needs-tasks";
import { ProformaApprovalCard } from "./proforma-approval-card";
import { NoticesPanel } from "./notices-panel";
import { ProposalsCard } from "./proposals-card";
import { DocumentsCard } from "./documents-card";
import { JourneyCard } from "./journey-card";
import { FooterActions } from "./footer-actions";

/**
 * `/portal/home` — the taxpayer dashboard. Refreshes the bound client + branding
 * on mount, then loads the client's real portal data (requested documents,
 * notices, proposals, shared documents) in parallel and composes it into the
 * welcome hero, checklist, task cards, case-status panel, and journey timeline.
 */
export function PortalHub() {
  const client = usePortalSessionStore((s) => s.client);
  const branding = usePortalSessionStore((s) => s.branding);
  const setClient = usePortalSessionStore((s) => s.setClient);
  const setBranding = usePortalSessionStore((s) => s.setBranding);

  const [loading, setLoading] = useState(true);
  const [requiredDocs, setRequiredDocs] = useState<PortalRequiredDocument[]>([]);
  const [notices, setNotices] = useState<PortalNotice[]>([]);
  const [proformas, setProformas] = useState<PortalProforma[]>([]);
  const [documents, setDocuments] = useState<PortalDocument[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await portalMe();
        if (!active) return;
        setClient(me.client);
        setBranding(me.branding);
      } catch {
        /* a 401 clears the session in the api layer; the guard redirects */
      }
      // Load every section in parallel; a failed card just stays empty rather
      // than blocking the whole page or flooding the client with toasts.
      const [reqs, nots, props, docs] = await Promise.allSettled([
        listRequiredDocuments(),
        listNotices(),
        listProformas(),
        listPublishedDocuments(),
      ]);
      if (!active) return;
      if (reqs.status === "fulfilled") setRequiredDocs(reqs.value);
      if (nots.status === "fulfilled") setNotices(nots.value);
      if (props.status === "fulfilled") setProformas(props.value);
      if (docs.status === "fulfilled") setDocuments(docs.value);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [setClient, setBranding]);

  if (loading || !client) return <PageLoader />;

  const firmName = branding?.firmName ?? "";
  const { checklist, outstanding, journey } = deriveHub({ client, requiredDocs, proformas });
  const pendingDocs = requiredDocs.filter((d) => d.status === "PENDING");
  const hasTasks = pendingDocs.length > 0 || client.showFinancials;

  return (
    <div className="flex flex-col gap-8 py-2">
      <section className="grid items-stretch gap-6 lg:grid-cols-[1.3fr_1fr]">
        <WelcomeHero clientName={client.name} firmName={firmName} outstanding={outstanding} />
        <ChecklistCard items={checklist} />
      </section>

      {/* Engagement proposal (PI) awaiting the client's approval, when present. */}
      <ProformaApprovalCard />

      {hasTasks && (
        <NeedsTasks
          pendingDocs={pendingDocs}
          showFinancials={client.showFinancials}
          firmName={firmName}
        />
      )}

      <section className="grid gap-5 lg:grid-cols-[1.15fr_1fr]">
        <NoticesPanel notices={notices} firmName={firmName} phone={branding?.phone ?? ""} />
        <ProposalsCard proformas={proformas} />
      </section>

      <section className="grid gap-5 md:grid-cols-2">
        {client.showDocuments && (
          <DocumentsCard documents={documents} pendingDocs={pendingDocs} />
        )}
        <JourneyCard steps={journey} />
      </section>

      <FooterActions phone={branding?.phone ?? ""} />
    </div>
  );
}
